import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

export type Lang = "en" | "sw";

const en = {
  appName: "Smart Safiri",
  liveTracking: "Live trip tracking",
  switchLanguage: "Kiswahili",
  live: "Live",
  reconnecting: "Reconnecting…",
  offline: "Offline. Showing last known details",
  updatedAgo: "Updated {ago}",
  justNow: "just now",
  secondsAgo: "{n}s ago",
  minutesAgo: "{n} min ago",

  phaseFinding: "Finding a driver",
  phaseFindingSub: "We're matching this ride with a nearby driver. This page updates automatically.",
  phaseToPickup: "Driver is on the way",
  phaseToPickupSub: "Heading to the pickup point",
  phaseAtPickup: "Driver has arrived",
  phaseAtPickupSub: "Waiting at the pickup point",
  phaseOnTrip: "On the way",
  phaseOnTripSub: "Heading to the drop-off",
  phaseOnTripNextStop: "Heading to the next stop",
  phaseAtStop: "Stopped at a stop",
  phaseAtStopSub: "The ride will continue shortly",
  phaseArrived: "Arrived at the drop-off",
  phaseArrivedSub: "Finishing up payment",
  phaseCompleted: "Trip completed",
  phaseCompletedSub: "Arrived safely",
  phaseCancelled: "Trip cancelled",
  phaseCancelledSub: "This trip was cancelled and is no longer being tracked.",
  phaseNoDriver: "No driver found",
  phaseNoDriverSub: "No driver was available for this request.",
  etaMinutes: "{n} min",
  arrivesIn: "Arrives in",
  since: "since {time}",
  at: "at {time}",

  driver: "Driver",
  rider: "Rider",
  callDriver: "Call driver",
  callRider: "Call rider",
  plateHint: "Check the number plate before getting in",
  rating: "{rating} rating",

  route: "Route",
  pickup: "Pickup",
  dropoff: "Drop-off",
  stopN: "Stop {n}",
  next: "Next",
  done: "Done",
  skipped: "Skipped",

  fare: "Fare",
  estimatedFare: "Estimated fare",
  totalFare: "Total fare",
  paymentMethod: "Payment",
  cash: "Cash",
  online: "Online",
  wallet: "Wallet",
  fareNote: "The final fare is confirmed at drop-off.",

  safety: "Safety",
  emergency: "Emergency",
  callEmergency: "Call {number}",
  share: "Share this page",
  copied: "Link copied",
  recenter: "Re-centre map",
  mapUnavailable: "The map can't be shown right now. Trip details below are still live.",

  timelineAccepted: "Driver assigned",
  timelineArrived: "Driver arrived",
  timelinePickedUp: "Picked up",
  timelineDroppedOff: "Dropped off",

  tripRef: "Trip {ref}",
  notFoundTitle: "This link isn't working",
  notFoundBody: "The trip may have ended more than 30 days ago, or the link is incomplete. Ask the person who shared it to send it again.",
  errorTitle: "Couldn't load the trip",
  errorBody: "Check your connection and try again.",
  retry: "Try again",
  landingTitle: "Track a Smart Safiri ride",
  landingBody: "Open the tracking link you received by SMS or WhatsApp to follow a ride live.",
  poweredBy: "Tanzania's Own, Safety Known",
};

type Dict = typeof en;

const sw: Dict = {
  appName: "Smart Safiri",
  liveTracking: "Kufuatilia safari moja kwa moja",
  switchLanguage: "English",
  live: "Moja kwa moja",
  reconnecting: "Inaunganisha upya…",
  offline: "Huna mtandao. Taarifa za mwisho zinaonyeshwa",
  updatedAgo: "Imesasishwa {ago}",
  justNow: "sasa hivi",
  secondsAgo: "sekunde {n} zilizopita",
  minutesAgo: "dakika {n} zilizopita",

  phaseFinding: "Tunatafuta dereva",
  phaseFindingSub: "Tunaunganisha safari hii na dereva wa karibu. Ukurasa huu unajisasisha wenyewe.",
  phaseToPickup: "Dereva yuko njiani",
  phaseToPickupSub: "Anaelekea mahali pa kuchukua abiria",
  phaseAtPickup: "Dereva amefika",
  phaseAtPickupSub: "Anasubiri mahali pa kuchukua abiria",
  phaseOnTrip: "Safari inaendelea",
  phaseOnTripSub: "Inaelekea mwisho wa safari",
  phaseOnTripNextStop: "Inaelekea kituo kinachofuata",
  phaseAtStop: "Imesimama kituoni",
  phaseAtStopSub: "Safari itaendelea muda mfupi",
  phaseArrived: "Imefika mwisho wa safari",
  phaseArrivedSub: "Malipo yanakamilishwa",
  phaseCompleted: "Safari imekamilika",
  phaseCompletedSub: "Imefika salama",
  phaseCancelled: "Safari imeghairiwa",
  phaseCancelledSub: "Safari hii imeghairiwa na haifuatiliwi tena.",
  phaseNoDriver: "Hakuna dereva aliyepatikana",
  phaseNoDriverSub: "Hakukuwa na dereva wa kupokea ombi hili.",
  etaMinutes: "dak {n}",
  arrivesIn: "Atafika baada ya",
  since: "tangu {time}",
  at: "saa {time}",

  driver: "Dereva",
  rider: "Abiria",
  callDriver: "Mpigie dereva",
  callRider: "Mpigie abiria",
  plateHint: "Hakikisha namba ya gari kabla ya kupanda",
  rating: "Ukadiriaji {rating}",

  route: "Njia",
  pickup: "Mahali pa kuchukuliwa",
  dropoff: "Mwisho wa safari",
  stopN: "Kituo {n}",
  next: "Kinachofuata",
  done: "Imepita",
  skipped: "Kimerukwa",

  fare: "Nauli",
  estimatedFare: "Nauli inayokadiriwa",
  totalFare: "Jumla ya nauli",
  paymentMethod: "Malipo",
  cash: "Taslimu",
  online: "Mtandaoni",
  wallet: "Pochi",
  fareNote: "Nauli kamili inathibitishwa mwisho wa safari.",

  safety: "Usalama",
  emergency: "Dharura",
  callEmergency: "Piga {number}",
  share: "Shiriki ukurasa huu",
  copied: "Kiungo kimenakiliwa",
  recenter: "Rudisha ramani katikati",
  mapUnavailable: "Ramani haiwezi kuonyeshwa kwa sasa. Taarifa za safari hapa chini bado zinasasishwa.",

  timelineAccepted: "Dereva amepatikana",
  timelineArrived: "Dereva amefika",
  timelinePickedUp: "Abiria amechukuliwa",
  timelineDroppedOff: "Abiria amefikishwa",

  tripRef: "Safari {ref}",
  notFoundTitle: "Kiungo hiki hakifanyi kazi",
  notFoundBody: "Huenda safari iliisha zaidi ya siku 30 zilizopita, au kiungo hakijakamilika. Mwombe aliyekutumia akutumie tena.",
  errorTitle: "Imeshindikana kupakia safari",
  errorBody: "Angalia mtandao wako kisha ujaribu tena.",
  retry: "Jaribu tena",
  landingTitle: "Fuatilia safari ya Smart Safiri",
  landingBody: "Fungua kiungo cha kufuatilia ulichopokea kwa SMS au WhatsApp ili kufuatilia safari moja kwa moja.",
  poweredBy: "Tanzania's Own, Safety Known",
};

export type MessageKey = keyof Dict;

const dictionaries: Record<Lang, Dict> = { en, sw };

function detectLang(): Lang {
  try {
    const saved = window.localStorage.getItem("safiri-track-lang");
    if (saved === "en" || saved === "sw") return saved;
  } catch {
    // Storage can be unavailable (private mode); fall through to the browser.
  }
  return navigator.language?.toLowerCase().startsWith("sw") ? "sw" : "en";
}

interface I18nValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(detectLang);

  const value = useMemo<I18nValue>(() => {
    const dict = dictionaries[lang];
    return {
      lang,
      setLang: (next) => {
        setLangState(next);
        document.documentElement.lang = next;
        try {
          window.localStorage.setItem("safiri-track-lang", next);
        } catch {
          // Preference just won't persist.
        }
      },
      t: (key, vars) =>
        Object.entries(vars ?? {}).reduce(
          (text, [name, val]) => text.replace(`{${name}}`, String(val)),
          dict[key]
        ),
    };
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}
